"use client";

import { useEffect, useRef, useState } from "react";
import { acceptAll, declineAll, savePreferences, useConsent } from "./consent-store";

interface Category {
  key: "essential" | "analytics" | "marketing" | "preferences";
  name: string;
  locked?: boolean;
  description: string;
}

const CATEGORIES: Category[] = [
  {
    key: "essential",
    name: "Essenciais",
    locked: true,
    description: "Necessários para o funcionamento do site: sessão, segurança, balanceamento de carga e suas escolhas de privacidade. Sempre ativos.",
  },
  {
    key: "analytics",
    name: "Analíticos / Desempenho",
    description: "Medem o uso do site de forma agregada (páginas vistas, origem do tráfego, erros) para melhorarmos o conteúdo. Ex.: Google Analytics, Microsoft Clarity, Hotjar.",
  },
  {
    key: "marketing",
    name: "Marketing",
    description: "Permitem mensurar campanhas e exibir conteúdo relevante dentro e fora do site. Ex.: Google Tag Manager, Meta Pixel, Google Ads, LinkedIn, TikTok.",
  },
  {
    key: "preferences",
    name: "Preferências",
    description: "Memorizam personalizações e escolhas de navegação para uma experiência mais consistente entre visitas.",
  },
];

/**
 * Granular cookie-preferences modal. Reads the current decision from the
 * store; "Salvar" persists exactly the toggled categories. Includes scroll
 * lock, focus trap and Escape-to-close for accessibility.
 */
export function CookiePreferencesModal({
  policyVersion,
  onClose,
}: {
  policyVersion: string;
  onClose: () => void;
}) {
  const { preferences } = useConsent();
  const [state, setState] = useState({
    analytics: preferences.analytics,
    marketing: preferences.marketing,
    preferences: preferences.preferences,
  });
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const restoreFocus = document.activeElement as HTMLElement | null;
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";

    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = panel.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKey);
    panelRef.current?.querySelector<HTMLElement>("button, input")?.focus();

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
      restoreFocus?.focus?.();
    };
  }, [onClose]);

  function toggle(key: "analytics" | "marketing" | "preferences") {
    setState((s) => ({ ...s, [key]: !s[key] }));
  }

  return (
    <div className="lgpd-modal" role="dialog" aria-modal="true" aria-labelledby="lgpd-modal-title">
      <div className="lgpd-modal__backdrop" onClick={onClose} aria-hidden />
      <div className="lgpd-modal__panel" ref={panelRef}>
        <div className="lgpd-modal__head">
          <h2 id="lgpd-modal-title">Preferências de privacidade</h2>
          <button type="button" className="lgpd-modal__close" onClick={onClose} aria-label="Fechar">
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
              <path fill="currentColor" d="M18.3 5.7 12 12l6.3 6.3-1.4 1.4L12 13.4 5.7 19.7l-1.4-1.4L10.6 12 4.3 5.7l1.4-1.4L12 10.6l6.3-6.3 1.4 1.4Z" />
            </svg>
          </button>
        </div>
        <p className="lgpd-modal__lead">
          Escolha quais categorias de cookies você autoriza. Os cookies essenciais não podem ser
          desativados. Você pode rever esta decisão a qualquer momento pelo botão de privacidade.
        </p>
        <ul className="lgpd-cats">
          {CATEGORIES.map((cat) => {
            const checked = cat.locked ? true : state[cat.key as "analytics" | "marketing" | "preferences"];
            return (
              <li key={cat.key} className="lgpd-cat">
                <div className="lgpd-cat__text">
                  <strong>
                    {cat.name}
                    {cat.locked ? <span className="lgpd-cat__tag">Sempre ativo</span> : null}
                  </strong>
                  <span>{cat.description}</span>
                </div>
                <label className={`lgpd-switch${cat.locked ? " lgpd-switch--locked" : ""}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={cat.locked}
                    aria-label={cat.name}
                    onChange={() =>
                      !cat.locked && toggle(cat.key as "analytics" | "marketing" | "preferences")
                    }
                  />
                  <span className="lgpd-switch__track" aria-hidden />
                </label>
              </li>
            );
          })}
        </ul>
        <div className="lgpd-modal__actions">
          <button
            type="button"
            className="lgpd-btn lgpd-btn--ghost"
            onClick={() => {
              declineAll(policyVersion, "modal");
              onClose();
            }}
          >
            Recusar tudo
          </button>
          <button
            type="button"
            className="lgpd-btn lgpd-btn--ghost"
            onClick={() => {
              savePreferences(state, policyVersion, "modal");
              onClose();
            }}
          >
            Salvar escolhas
          </button>
          <button
            type="button"
            className="lgpd-btn lgpd-btn--primary"
            onClick={() => {
              acceptAll(policyVersion, "modal");
              onClose();
            }}
          >
            Aceitar tudo
          </button>
        </div>
      </div>
    </div>
  );
}
